import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';

/**
 * TopRotatingBanner Component
 *
 * Compact gray strip with an inner rounded card (the square-like structure).
 * Copy is swapped per breakpoint so text is never truncated with ellipsis.
 */
const TopRotatingBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const bannerRef = useRef(null);

  const cards = [
    {
      title: 'Still Running Without AI Agents?',
      tabletTitle: 'No AI Agents Yet?',
      mobileTitle: 'No AI Agents Yet?',
      subtitle: 'Automate Multi-Step Work',
      tabletSubtitle: 'Automate multi-step work',
      mobileSubtitle: 'Automate work with AI',
      description: 'Free AI Agent Audit for copilots, workflows, and guardrails.',
      tabletDescription: 'Free AI Agent Audit for copilots & workflows.',
      icon: 'Bot',
      gradient: 'from-[#00d690] via-[#00b876] to-[#009966]',
      cardBg: 'from-[#0a1929] via-[#112240] to-[#1a2f4a]',
      cardBorder: 'border-[#00d690]/40',
      accentColor: '#00d690',
      cta: 'Free AI Audit',
      ctaTablet: 'AI Audit',
      ctaMobile: 'Audit',
      action: () => {
        window.open('https://cal.com/octalchip/30min', '_blank');
      }
    },
    {
      title: 'Docs Everywhere, Answers Nowhere?',
      tabletTitle: 'Knowledge Stuck in Docs?',
      mobileTitle: 'Knowledge Stuck?',
      subtitle: 'Ship Enterprise RAG Fast',
      tabletSubtitle: 'Ship enterprise RAG fast',
      mobileSubtitle: 'Ship RAG assistants',
      description: 'Citation-ready RAG assistants with evals and secure deploy.',
      tabletDescription: 'RAG assistants with citations, evals & secure deploy.',
      icon: 'Database',
      gradient: 'from-[#6366f1] via-[#4f46e5] to-[#4338ca]',
      cardBg: 'from-[#1e1b4b] via-[#312e81] to-[#4338ca]',
      cardBorder: 'border-[#6366f1]/40',
      accentColor: '#6366f1',
      cta: 'View AI Services',
      ctaTablet: 'AI Services',
      ctaMobile: 'Services',
      action: () => {
        window.location.href = '/ai-ml-services';
      }
    },
    {
      title: 'GenAI Pilots Stuck in Demo Mode?',
      tabletTitle: 'GenAI Stuck in Demo?',
      mobileTitle: 'GenAI Stuck?',
      subtitle: 'Move to Production LLMOps',
      tabletSubtitle: 'Move to production LLMOps',
      mobileSubtitle: 'Ship production LLMOps',
      description: 'Production blueprint for evals, guardrails, and cost control.',
      tabletDescription: 'Blueprint for evals, guardrails & cost control.',
      icon: 'Sparkles',
      gradient: 'from-[#06b6d4] via-[#0891b2] to-[#0e7490]',
      cardBg: 'from-[#083344] via-[#164e63] to-[#155e75]',
      cardBorder: 'border-[#06b6d4]/40',
      accentColor: '#06b6d4',
      cta: 'AI Blueprint',
      ctaTablet: 'Blueprint',
      ctaMobile: 'Plan',
      action: () => {
        window.location.href = '/services/ai-integration';
      }
    }
  ];

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
    }, 14000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [cards.length]);

  // Keep layout offsets in sync with real banner height
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return undefined;

    const syncHeight = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--banner-height', `${height}px`);
        const headerVar = getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height')
          .trim();
        const headerHeight = parseFloat(headerVar) || 112;
        document.documentElement.style.setProperty(
          '--total-fixed-height',
          `${height + headerHeight}px`
        );
      }
    };

    syncHeight();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncHeight) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncHeight);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncHeight);
    };
  }, []);

  const cardVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.8
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.8
    })
  };

  const transition = {
    type: 'spring',
    stiffness: 300,
    damping: 30,
    duration: 0.6
  };

  const currentCard = cards[currentIndex];
  const direction = 1;

  return (
    <section
      ref={bannerRef}
      className="w-full fixed top-0 left-0 right-0 z-[1002] overflow-hidden"
      aria-label="Rotating promotional cards"
      role="region"
      style={{
        zIndex: 1002,
        top: 0,
        left: 0,
        right: 0
      }}
    >
      <div className="relative h-[60px] sm:h-[65px] md:h-[70px] lg:h-[80px] overflow-hidden">
        {/* Banner strip background - gray */}
        <div className="absolute inset-0 bg-gray-200 transition-all duration-700" />

        {/* Banner strip decorative elements */}
        <div className="absolute inset-0 overflow-hidden" />

        {/* Card content - image-like composition */}
        <div className="relative h-full flex items-center justify-center z-10">
          <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                className={`relative flex items-center justify-between gap-2 sm:gap-3 md:gap-4 lg:gap-4 xl:gap-6 max-w-7xl mx-auto w-full bg-gradient-to-r ${currentCard.cardBg} rounded-lg sm:rounded-xl px-3 sm:px-4 md:px-6 lg:px-6 xl:px-8 py-2 sm:py-2.5 md:py-3 lg:py-4 border ${currentCard.cardBorder} shadow-2xl backdrop-blur-sm overflow-hidden`}
                style={{
                  boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 40px ${currentCard.accentColor}20`
                }}
              >
                {/* Card-specific decorative elements */}
                <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                  <div
                    className="absolute -right-10 -top-5 w-32 h-32 rounded-full opacity-20 blur-2xl transition-all duration-700"
                    style={{
                      background: `radial-gradient(circle, ${currentCard.accentColor}60, transparent)`,
                      transform: `scale(${1 + Math.sin(currentIndex * 2) * 0.2})`
                    }}
                  />
                  <div
                    className="absolute -left-5 -bottom-5 w-24 h-24 rounded-full opacity-15 blur-xl transition-all duration-700"
                    style={{
                      background: `radial-gradient(circle, ${currentCard.accentColor}80, transparent)`,
                      transform: `scale(${1 + Math.cos(currentIndex * 2) * 0.15})`
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent" />
                  <div
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                      backgroundImage: `linear-gradient(${currentCard.accentColor}30 1px, transparent 1px), linear-gradient(90deg, ${currentCard.accentColor}30 1px, transparent 1px)`,
                      backgroundSize: '15px 15px'
                    }}
                  />
                </div>

                {/* Left side - Icon square and Title */}
                <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-3 xl:gap-4 flex-1 relative z-20 min-w-0">
                  <div
                    className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-8 sm:h-10 md:h-12 bg-white/30 rounded-full hidden sm:block"
                    style={{
                      boxShadow: `0 0 10px ${currentCard.accentColor}80`
                    }}
                  />

                  <motion.div
                    className="relative z-10 flex-shrink-0"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                  >
                    {/* Icon square tile */}
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gradient-to-br ${currentCard.gradient} rounded-lg sm:rounded-xl flex items-center justify-center shadow-2xl relative overflow-hidden border-2 border-white/20`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                      <div className="absolute inset-1 bg-white/10 rounded-md sm:rounded-lg" />
                      <Icon
                        name={currentCard.icon}
                        size={16}
                        className="sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-white relative z-10 drop-shadow-lg"
                      />
                    </div>
                    {/* Outer glow on square */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${currentCard.gradient} rounded-lg sm:rounded-xl blur-xl opacity-60 -z-10 animate-pulse`}
                      style={{
                        filter: `blur(20px) drop-shadow(0 0 15px ${currentCard.accentColor}60)`
                      }}
                    />
                  </motion.div>

                  <div className="flex flex-col relative z-10 min-w-0 max-w-full">
                    <motion.h2
                      className="text-[11px] sm:text-sm md:text-base lg:text-xl xl:text-2xl font-bold text-white leading-tight drop-shadow-lg break-words pr-1"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      style={{
                        textShadow: `0 2px 10px rgba(0,0,0,0.3), 0 0 20px ${currentCard.accentColor}40`
                      }}
                    >
                      <span className="sm:hidden">{currentCard.mobileTitle}</span>
                      <span className="hidden sm:inline md:hidden">{currentCard.tabletTitle || currentCard.mobileTitle}</span>
                      <span className="hidden md:inline">{currentCard.title}</span>
                    </motion.h2>
                    <motion.p
                      className="text-[9px] sm:text-xs md:text-xs lg:text-sm text-white/90 font-medium leading-tight break-words pr-1 block"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      style={{
                        textShadow: '0 1px 5px rgba(0,0,0,0.2)'
                      }}
                    >
                      <span className="sm:hidden">{currentCard.mobileSubtitle}</span>
                      <span className="hidden sm:inline md:hidden">{currentCard.tabletSubtitle || currentCard.mobileSubtitle}</span>
                      <span className="hidden md:inline">{currentCard.subtitle}</span>
                    </motion.p>
                  </div>
                </div>

                {/* Center description */}
                <motion.div
                  className="flex-1 text-center hidden md:block relative z-20 min-w-0 px-1 sm:px-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <p
                    className="text-[10px] sm:text-xs md:text-xs lg:text-sm text-white/90 leading-tight mx-auto font-medium"
                    style={{
                      textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}
                  >
                    <span className="lg:hidden">{currentCard.tabletDescription || currentCard.description}</span>
                    <span className="hidden lg:inline">{currentCard.description}</span>
                  </p>
                </motion.div>

                {/* CTA */}
                <motion.div
                  className="flex-shrink-0 relative z-20 ml-1"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, type: 'spring' }}
                >
                  <button
                    onClick={currentCard.action}
                    className="bg-white/95 backdrop-blur-sm text-gray-900 px-2 sm:px-3 md:px-4 lg:px-5 py-1 sm:py-1.5 md:py-1.5 lg:py-2 text-[10px] sm:text-xs md:text-xs lg:text-sm font-bold rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 relative overflow-hidden group border border-white/50 btn-animate-float whitespace-nowrap"
                    style={{
                      boxShadow: `0 4px 15px rgba(0,0,0,0.2), 0 0 20px ${currentCard.accentColor}30`
                    }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${currentCard.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    <span className="relative z-10 flex items-center gap-1 sm:gap-1.5 transition-colors duration-300 group-hover:text-white font-semibold">
                      <span className="sm:hidden">{currentCard.ctaMobile}</span>
                      <span className="hidden sm:inline lg:hidden">{currentCard.ctaTablet || currentCard.cta}</span>
                      <span className="hidden lg:inline">{currentCard.cta}</span>
                      <Icon name="ArrowRight" size={10} className="sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TopRotatingBanner;
